import React, { useRef, useState, useCallback } from "react";
import { Upload, X, FileText, Image, File } from "lucide-react";
import "./FileUpload.scss";

const FILE_ICONS = {
  "application/pdf": FileText,
  "text/plain": FileText,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  "image/webp": Image,
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * FileUpload — Reusable drag-and-drop file upload with preview.
 *
 * Props:
 *   files         — array of File objects (controlled)
 *   onChange      — (files: File[]) => void
 *   accept        — accept string (default: common doc/image types)
 *   multiple      — allow multiple files (default: true)
 *   maxFiles      — max file count (default: 5)
 *   maxSizeMB     — max size per file in MB (default: 10)
 *   disabled      — disable the upload area
 *   className     — extra class
 *   label         — label text above the drop zone
 *   hint          — hint text inside the drop zone
 */
const FileUpload = ({
  files = [],
  onChange,
  accept = ".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png,.webp",
  multiple = true,
  maxFiles = 5,
  maxSizeMB = 10,
  disabled = false,
  className = "",
  label,
  hint,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const maxBytes = maxSizeMB * 1024 * 1024;

  const validateAndAdd = useCallback(
    (incoming) => {
      setError("");
      const valid = [];
      for (const f of incoming) {
        if (files.length + valid.length >= maxFiles) {
          setError(`Maximum ${maxFiles} files allowed.`);
          break;
        }
        if (f.size > maxBytes) {
          setError(`"${f.name}" exceeds ${maxSizeMB} MB limit.`);
          continue;
        }
        // Deduplicate by name+size
        const dup = files.find((ex) => ex.name === f.name && ex.size === f.size);
        if (dup) continue;
        valid.push(f);
      }
      if (valid.length) onChange([...files, ...valid]);
    },
    [files, onChange, maxFiles, maxBytes, maxSizeMB]
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) {
      validateAndAdd(Array.from(e.dataTransfer.files));
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) {
      validateAndAdd(Array.from(e.target.files));
    }
    e.target.value = "";
  };

  const removeFile = (idx) => {
    if (disabled) return;
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
    setError("");
  };

  const getIcon = (file) => {
    const Icon = FILE_ICONS[file.type] || File;
    return <Icon size={18} />;
  };

  return (
    <div className={`file-upload ${className}`}>
      {label && <label className="file-upload__label">{label}</label>}

      <div
        className={`file-upload__zone ${dragActive ? "file-upload__zone--active" : ""} ${disabled ? "file-upload__zone--disabled" : ""}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && !disabled && inputRef.current?.click()}
      >
        <Upload size={24} className="file-upload__zone-icon" />
        <span className="file-upload__zone-text">
          {hint || "Drag & drop files here, or click to browse"}
        </span>
        <span className="file-upload__zone-hint">
          PDF, TXT, DOC, DOCX, JPG, PNG — Max {maxSizeMB} MB each
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className="file-upload__input"
          disabled={disabled}
        />
      </div>

      {error && <div className="file-upload__error">{error}</div>}

      {files.length > 0 && (
        <ul className="file-upload__list">
          {files.map((file, idx) => (
            <li key={`${file.name}-${idx}`} className="file-upload__item">
              <span className="file-upload__item-icon">{getIcon(file)}</span>
              <span className="file-upload__item-name" title={file.name}>
                {file.name}
              </span>
              <span className="file-upload__item-size">{formatFileSize(file.size)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="file-upload__item-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(idx);
                  }}
                  title="Remove"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
